import api from './api';
import type { PaginatedResponse } from './transformers';
import type {
  Appointment,
  AppointmentCalendar,
  AppointmentCalendarClonePayload,
  AppointmentCalendarPayload,
  AppointmentCancelPayload,
  AppointmentPayload,
  AppointmentUpdatePayload,
  AppointmentStatus,
  AvailabilityException,
  AvailabilityExceptionPayload,
  AvailabilityRule,
  AvailabilityRulePayload,
} from '../types';

export interface ListAppointmentCalendarsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
  ordering?: string;
}

export interface ListAvailabilityExceptionsParams {
  date?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface ListAppointmentsParams {
  page?: number;
  pageSize?: number;
  calendarId?: string;
  status?: AppointmentStatus;
  startsAtFrom?: string;
  startsAtTo?: string;
  search?: string;
}

function buildCalendarListParams(params: ListAppointmentCalendarsParams = {}) {
  return {
    page: params.page,
    page_size: params.pageSize,
    search: params.search || undefined,
    is_active: params.isActive,
    ordering: params.ordering,
  };
}

function buildExceptionListParams(params: ListAvailabilityExceptionsParams = {}) {
  return {
    date: params.date,
    date_from: params.dateFrom,
    date_to: params.dateTo,
  };
}

function buildAppointmentListParams(params: ListAppointmentsParams = {}) {
  return {
    page: params.page,
    page_size: params.pageSize,
    calendar_id: params.calendarId,
    status: params.status,
    starts_at_from: params.startsAtFrom,
    starts_at_to: params.startsAtTo,
    search: params.search || undefined,
  };
}

export async function listAppointmentCalendars(
  params: ListAppointmentCalendarsParams = {},
): Promise<PaginatedResponse<AppointmentCalendar>> {
  const response = await api.get('/api/appointments/calendars/', {
    params: buildCalendarListParams(params),
  });
  return response.data as PaginatedResponse<AppointmentCalendar>;
}

export async function fetchAppointmentCalendar(calendarId: string): Promise<AppointmentCalendar> {
  const response = await api.get(`/api/appointments/calendars/${calendarId}/`);
  return response.data as AppointmentCalendar;
}

export async function listAppointments(
  params: ListAppointmentsParams = {},
): Promise<PaginatedResponse<Appointment>> {
  const response = await api.get('/api/appointments/appointments/', {
    params: buildAppointmentListParams(params),
  });
  return response.data as PaginatedResponse<Appointment>;
}

export async function createAppointment(payload: AppointmentPayload): Promise<Appointment> {
  const response = await api.post('/api/appointments/appointments/', payload);
  return response.data as Appointment;
}

export async function updateAppointment(
  appointmentId: string,
  payload: AppointmentUpdatePayload,
): Promise<Appointment> {
  const response = await api.patch(`/api/appointments/appointments/${appointmentId}/`, payload);
  return response.data as Appointment;
}

export async function cancelAppointment(
  appointmentId: string,
  payload: AppointmentCancelPayload,
): Promise<Appointment> {
  const response = await api.post(`/api/appointments/appointments/${appointmentId}/cancel/`, payload);
  return response.data as Appointment;
}

export async function createAppointmentCalendar(
  payload: AppointmentCalendarPayload,
): Promise<AppointmentCalendar> {
  const response = await api.post('/api/appointments/calendars/', payload);
  return response.data as AppointmentCalendar;
}

export async function cloneAppointmentCalendar(
  calendarId: string,
  payload: AppointmentCalendarClonePayload,
): Promise<AppointmentCalendar> {
  const response = await api.post(`/api/appointments/calendars/${calendarId}/clone/`, payload);
  return response.data as AppointmentCalendar;
}

export async function updateAppointmentCalendar(
  calendarId: string,
  payload: Partial<AppointmentCalendarPayload>,
): Promise<AppointmentCalendar> {
  const response = await api.patch(`/api/appointments/calendars/${calendarId}/`, payload);
  return response.data as AppointmentCalendar;
}

export async function listAvailabilityRules(calendarId: string): Promise<AvailabilityRule[]> {
  const response = await api.get(`/api/appointments/calendars/${calendarId}/availability-rules/`);
  return Array.isArray(response.data) ? response.data as AvailabilityRule[] : [];
}

export async function createAvailabilityRule(
  calendarId: string,
  payload: AvailabilityRulePayload,
): Promise<AvailabilityRule> {
  const response = await api.post(
    `/api/appointments/calendars/${calendarId}/availability-rules/`,
    payload,
  );
  return response.data as AvailabilityRule;
}

export async function updateAvailabilityRule(
  ruleId: string,
  payload: Partial<AvailabilityRulePayload>,
): Promise<AvailabilityRule> {
  const response = await api.patch(`/api/appointments/availability-rules/${ruleId}/`, payload);
  return response.data as AvailabilityRule;
}

export async function deleteAvailabilityRule(ruleId: string): Promise<void> {
  await api.delete(`/api/appointments/availability-rules/${ruleId}/`);
}

export async function listAvailabilityExceptions(
  calendarId: string,
  params: ListAvailabilityExceptionsParams = {},
): Promise<AvailabilityException[]> {
  const response = await api.get(`/api/appointments/calendars/${calendarId}/exceptions/`, {
    params: buildExceptionListParams(params),
  });
  return Array.isArray(response.data) ? response.data as AvailabilityException[] : [];
}

export async function createAvailabilityException(
  calendarId: string,
  payload: AvailabilityExceptionPayload,
): Promise<AvailabilityException> {
  const response = await api.post(
    `/api/appointments/calendars/${calendarId}/exceptions/`,
    payload,
  );
  return response.data as AvailabilityException;
}

export async function updateAvailabilityException(
  exceptionId: string,
  payload: Partial<AvailabilityExceptionPayload>,
): Promise<AvailabilityException> {
  const response = await api.patch(`/api/appointments/exceptions/${exceptionId}/`, payload);
  return response.data as AvailabilityException;
}

export async function deleteAvailabilityException(exceptionId: string): Promise<void> {
  await api.delete(`/api/appointments/exceptions/${exceptionId}/`);
}
